// Spec 45 component-behavior — cmp_ds_input.
//
// Variants: default / search / password. Sizes: sm / md / lg.
// 9-state matrix (default/hover/focus/filled/disabled/readonly/error/
// error_focus/loading) per spec 45.
//
// Key behaviors per spec:
//   * Native <input> with the appropriate type. Variant just adjusts
//     type + visual affordances (search → magnifier prefix; password →
//     visibility toggle).
//   * error=true sets aria-invalid + lets the consumer bind
//     aria-describedby to its error element via the standard prop.
//   * loading sets aria-busy.
//   * Required marker is consumer-rendered (FormField wrapper, Phase
//     4b.X); the Input itself just propagates aria-required.
//   * Password visibility is CONTROLLED externally via `revealed`
//     prop. Compose with IconButton (the eye toggle) at FormField
//     wrapper level so the toggle's own a11y state (aria-pressed)
//     stays under the consumer's control.
//   * search variant Escape-clears: the spec model says Escape clears
//     value when non-empty. Implemented via dispatching a native
//     input event so controlled-component consumers see the change.

import { forwardRef, useCallback, type InputHTMLAttributes, type KeyboardEvent } from "react";

import { cx } from "../lib/cx.ts";

export type InputVariant = "default" | "search" | "password";
export type InputSize = "sm" | "md" | "lg";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
  readonly variant?: InputVariant;
  readonly size?: InputSize;
  readonly error?: boolean;
  readonly loading?: boolean;
  /** When variant='password', controls type-toggling. Defaults to false (hidden). Caller wires the eye-icon button. */
  readonly revealed?: boolean;
  /** Optional override for the underlying input type. Useful when variant='default' but a more specific type fits (email/url/tel). */
  readonly type?: InputHTMLAttributes<HTMLInputElement>["type"];
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = "default",
      size = "md",
      error = false,
      loading = false,
      revealed = false,
      type,
      readOnly,
      disabled,
      className,
      onKeyDown,
      ...rest
    },
    ref,
  ) => {
    // Resolve the underlying input type per variant. Caller-supplied
    // `type` always wins so a consumer can opt in to e.g. type='email'
    // on the default variant.
    let resolvedType: InputHTMLAttributes<HTMLInputElement>["type"] = type;
    if (resolvedType === undefined) {
      if (variant === "search") resolvedType = "search";
      else if (variant === "password") resolvedType = revealed ? "text" : "password";
      else resolvedType = "text";
    }

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        // search variant: Escape clears the value (spec 45 cmp_ds_input
        // keyboard model). We dispatch a synthetic input event to keep
        // controlled-component consumers informed.
        if (variant === "search" && event.key === "Escape") {
          const el = event.currentTarget;
          if (el.value.length > 0) {
            event.preventDefault();
            // Use the native setter to bypass React's value-tracking
            // shortcut — otherwise React thinks the value didn't change
            // and skips firing onChange on controlled inputs.
            const nativeSetter = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              "value",
            )?.set;
            nativeSetter?.call(el, "");
            el.dispatchEvent(new Event("input", { bubbles: true }));
          }
        }
        onKeyDown?.(event);
      },
      [variant, onKeyDown],
    );

    return (
      <input
        ref={ref}
        type={resolvedType}
        data-component="input"
        data-variant={variant}
        data-size={size}
        data-error={error || undefined}
        data-revealed={variant === "password" ? revealed : undefined}
        aria-invalid={error || undefined}
        aria-busy={loading || undefined}
        aria-disabled={disabled === true ? true : undefined}
        readOnly={readOnly}
        disabled={disabled === true}
        className={cx("nai-input", className)}
        onKeyDown={handleKeyDown}
        {...rest}
      />
    );
  },
);

Input.displayName = "Input";
